import { useCallback } from "react";
import { useApplicationModals } from "app/common/hooks";
import { Badge } from "app/components/atoms/badge";
import { Button } from "app/components/atoms/button";
import Table from "app/components/atoms/table";
import { InputMolecule } from "app/components/molecules/form";
import ImageWithPreview from "app/components/molecules/imageWithPreview";
import { ModalMolecule } from "app/components/molecules/modal";
import api, { productCode } from "app/integration/api";
import { useCollector, useUpdateForm } from "app/integration/common/hooks";
import { Formik } from "formik";
import { DateTime } from "luxon";
import { useEffect, useState, useRef } from "react";
import * as Yup from "yup";
import AppModals from ".";
import Resizer from "react-image-file-resizer";
import { formatShippingNotification, getTrackingUrl } from "lib/formatMessage";

function CreateDeliveryPackingModal({ onSuccess = () => { } }) {
  const [isLoading, setIsLoading] = useState(false);

  const { showStates, closeModal, getPayloads } = useApplicationModals();
  const payloads = getPayloads("deliveryOrder.delivery");

  const handleClose = () => {
    closeModal("deliveryOrder.delivery");
    onSuccess();
  };
  const handleSuccess = () => {
    closeModal("deliveryOrder.delivery");
    onSuccess();
  };

  const now = DateTime.now();
  const dt = now.toISODate();
  const time = now.toLocaleString(DateTime.TIME_24_SIMPLE);

  var isError = false;
  const { openModal, setPayloads } = useApplicationModals();

  const {
    initialValues,
    setInitialValues,
    validationSchema,
    onSubmit,
    setFieldValue,
  } = useUpdateForm({
    initialValues: {
      id: null,
      code: "",
      status: 3,
      file: null,
      image_date: "",
      packing_picture: null,
      resi_number: "",
      items: [],
    },
    validationSchema: Yup.object().shape({
      name: Yup.string(),
      // file: Yup.mixed().required("Harap upload foto hasil packing terlebih dahulu."),
      status: Yup.number(),
      file: Yup.mixed().when("status", (status, schema) => {
        if (status == 3) {
          return schema.required(
            "Harap upload foto hasil packing terlebih dahulu."
          );
        }
        return schema.notRequired();
      }),
      resi_number: Yup.string().when("status", (status, schema) => {
        if (status == 4) {
          return schema.required("Nomor Resi harus diisi.");
        }
        return schema;
      }),
    }),
    /**
     * Menangani submit formulir konfirmasi packing (status 3) atau konfirmasi pengiriman (status 4).
     * Mendukung item bertipe unique barcode maupun non-unique barcode secara berdampingan:
     * - Item unique barcode dengan product_code_id valid diperbarui status unit fisiknya ke 'delivery'.
     * - Item non-unique barcode (tanpa baris di tabel product_codes) diproses dengan aman tanpa memicu request PUT /product-code/null.
     *
     * @param {Object} values - Nilai formulir Formik ({ id, status, file, image_date, resi_number, items }).
     * @returns {Promise<void>}
     * @sideEffects Mengirim pembaruan Delivery Order ke API backend, memperbarui status unit ProductCode yang valid,
     *              dan menampilkan modal error jika request gagal.
     */
    onSubmit: async (values) => {
      try {
        if (values.status == 3) {
          await api.deliveryOrder.update({
            id: values.id,
            status: values.status,
            packing_picture: values.file,
            image_date: values.image_date,
          });
        } else if (values.status == 4) {
          await api.deliveryOrder.update({
            id: values.id,
            status: values.status,
            resi_number: values.resi_number,
          });
        }

        // Perbarui status unit ProductCode HANYA untuk produk unique barcode yang memiliki product_code_id valid.
        // Produk non-unique barcode tidak memiliki baris di tabel product_codes, sehingga dilewati agar
        // tidak memicu request PUT /product-code/null (yang memicu error "Please input a valid numeric value!").
        for (const item of values.items || []) {
          if (item?.product_code_id && !isNaN(Number(item.product_code_id))) {
            await api.productCode.update(
              item.product_code_id,
              {
                status: "delivery",
                scan_delivery_date: new Date(),
                sales_order_id: item.sales_order_id || payloads?.sales_order_id,
                sales_channel_id: item.sales_channel_id || payloads?.sales_channel_id,
              }
            );
          }
        }

        // Reset file ketika sukses
        values.file = null;

        isError = false;
      } catch (error) {
        setPayloads("errorMessage.view", {
          title: "Pengiriman Pesanan",
          message: error?.response?.data?.message
            ? error?.response?.data?.message
            : "Server Error",
        });
        isError = true;
        openModal("errorMessage.view");
      }
    },
    onSuccess: () => {
      // Reset initial values: file
      setInitialValues({
        ...initialValues,
        file: null,
      });

      if (!isError) {
        handleSuccess();
      }
    },
  });

  const resizeFile = (file) =>
    new Promise((resolve) => {
      Resizer.imageFileResizer(
        file,
        600,
        600,
        "JPEG",
        100,
        0,
        (uri) => {
          resolve(uri);
        },
        "file"
      );
    });

  const fileChangeHandler = async (e, index) => {
    let beforeResize = e.target.files[0];
    console.log("before resize file: ", beforeResize);
    let imageResize = await resizeFile(e.target.files[0]);

    console.log("after resize: ", imageResize);

    let fileType = imageResize.type;
    let fileName = imageResize.name;

    if (
      fileType.search(/jpeg|jpg|png/gi) >= 0 ||
      fileName.search(/.rar/gi) >= 0
    ) {
      console.log(
        "file ini di perbolehkan untuk di upload",
        fileType.search(/jpeg|jpg|pdf|xls|zip|rar/gi),
        fileName.search(/.rar/gi)
      );
      // const uploaded = await api.uploadImage.product({ image: imageResize });

      setInitialValues({
        ...initialValues,
        file: imageResize,
        image_date: beforeResize.lastModified,
      });
    } else {
      console.log(
        "file tidak dapat di upload.",
        fileType.search(/jpeg|jpg|pdf|xls|zip|rar/gi),
        fileName.search(/.rar/gi)
      );
      alert(`Jenis File ${fileType} tidak dapat di upload`);
    }
  };

  useEffect(() => {
    setInitialValues({
      ...initialValues,
      id: payloads?.id,
      code: payloads?.code,
      packing_picture: payloads?.packing_picture,
      image_date: payloads?.image_date,
      resi_number: payloads?.resi_number,
      status: payloads?.status == 2 ? 3 : 4,
      items: payloads?.OrderItems?.flatMap(item => {
        // Non-unique products never get a ProductCode row, so they're shown as
        // ONE summary row for the whole line qty instead of being split per
        // unit — mirrors the same branch in createPackingOrder.jsx. This modal
        // is read-only display (checkbox is always disabled, no validation on
        // `items`), so this only fixes the "shown as 1, 1, 1..." display bug,
        // not any submit-blocking behavior.
        if (item?.master_product?.is_unique_barcode === false) {
          return [{
            id: item?.id,
            product_name: item?.master_product?.name,
            imageUrl: item?.master_product?.imageUrl,
            code: item?.master_product?.code,
            size: item?.master_size?.name,
            color: item?.master_color?.name,
            qty: item?.qty,
            product_id: item?.product_id,
            color_variant_id: item?.color_variant_id,
            size_variant_id: item?.size_variant_id,
            sales_order_id: payloads?.sales_order_id,
            status_packing: item?.status_packing ?? false,
            product_code_id: null,
            sales_channel_id: payloads?.sales_channel_id,
            is_unique_barcode: false,
          }];
        }

        // Determine the number of items to process based on product codes
        const productCodesForItem = (payloads?.product_code || []).filter(pc =>
          pc?.product_variant?.product_id === item?.product_id &&
          pc?.product_variant?.size_variant_id === item?.size_variant_id &&
          pc?.product_variant?.color_variant_id === item?.color_variant_id
        );

        // Use the length of product codes or item quantity, whichever is smaller
        const processLength = Math.min(
          productCodesForItem?.length || 0,
          item?.qty || 0
        );

        return Array.from({ length: processLength }, (_, index) => ({
          id: item?.id,
          product_name: item?.master_product?.name,
          imageUrl: item?.master_product?.imageUrl,
          code: item?.master_product?.code,
          size: item?.master_size?.name,
          color: item?.master_color?.name,
          qty: 1,
          product_id: item?.product_id,
          color_variant_id: item?.color_variant_id,
          size_variant_id: item?.size_variant_id,
          sales_order_id: payloads?.sales_order_id,
          status_packing: true,
          product_code_id: productCodesForItem?.[index]?.id || null,
          sales_channel_id: payloads?.sales_channel_id,
          is_unique_barcode: true,
        })).concat(
          // Add remaining items without product code
          Array.from({ length: (item?.qty || 0) - processLength }, () => ({
            id: item?.id,
            product_name: item?.master_product?.name,
            imageUrl: item?.master_product?.imageUrl,
            code: item?.master_product?.code,
            size: item?.master_size?.name,
            color: item?.master_color?.name,
            qty: 1,
            product_id: item?.product_id,
            color_variant_id: item?.color_variant_id,
            size_variant_id: item?.size_variant_id,
            sales_order_id: payloads?.sales_order_id,
            status_packing: false,
            product_code_id: null,
            sales_channel_id: payloads?.sales_channel_id,
            is_unique_barcode: true,
          }))
        );
      }),
    });
  }, [payloads]);

  const getCourierName = (code) => {
    const courierMapping = {
      JAGOPACK: "LION - JAGOPACK",
      REGPACK: "LION - REGPACK",
      BIGPACK: "LION - BIGPACK",
      BOSSPACK: "LION - BOSSPACK",
      CTC: "JNE City Courier",
      JTR: "JNE Trucking",
      CTCYES: "JNE City Courier YES",
      EZ: "J&T Express"
    };
    return courierMapping[code] || code;
  };

  return (
    <ModalMolecule
      show={showStates["deliveryOrder.delivery"]}
      title="Packing Pengiriman"
      onClose={handleClose}
    >
      {isLoading && (
        <div className="fixed top-0 left-0 w-full h-full bg-gray-200 bg-opacity-50 z-50 flex justify-center items-center">
          <div className="bg-white p-4 rounded-md">
            <p>Loading...</p>
          </div>
        </div>
      )}
      <AppModals
        items={["errorMessage.view"]}
        onSuccess={{
          "errorMessage.view": () => fetch(),
        }}
      />
      <Formik
        {...{ initialValues, validationSchema, onSubmit }}
        enableReinitialize={true}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          setFieldValue,
        }) => (
          <form
            className="space-y-4"
            onSubmit={handleSubmit}
          >
            <div>{values.code}</div>
            <Table.Container>
              <Table.Wrapper asIndex={false}>
                <Table.Header>
                  <Table.HeaderRow>
                    <Table.Heading>Produk</Table.Heading>
                    <Table.Heading>Varian</Table.Heading>
                    <Table.Heading align="right">Qty</Table.Heading>
                    <Table.Heading align="right">Status</Table.Heading>
                    <Table.Heading align="right"></Table.Heading>
                  </Table.HeaderRow>
                </Table.Header>
                <Table.Body>
                  {values?.items
                    ?.sort((a, b) => a.id - b.id)
                    .map((item, index) => (
                      <Table.BodyRow key={index}>
                        <Table.Cell>
                          <div className="flex gap-2 items-center">
                            {item?.imageUrl && item?.imageUrl != "" ? (
                              <ImageWithPreview
                                size="lg"
                                src={`${process.env.REACT_APP_AWS_LINK_URL}${item?.imageUrl}`}
                              ></ImageWithPreview>
                            ) : null}
                            <div>
                              {item?.product_name}
                              <div>{item?.code}</div>
                            </div>
                          </div>
                        </Table.Cell>
                        <Table.Cell>
                          <div className="flex flex-wrap gap-2 items-center">
                            <Badge color="gray">{item?.size}</Badge>
                            <Badge color="gray">{item?.color}</Badge>
                          </div>
                        </Table.Cell>
                        <Table.Cell align="right">{item?.qty}</Table.Cell>
                        <Table.Cell align="right">
                          <Badge
                            color={item?.status_packing ? "green" : "gray"}
                          >
                            {item?.status_packing
                              ? "Sudah Packing"
                              : "Belum Packing"}
                          </Badge>
                        </Table.Cell>
                        <Table.Cell align="right">
                          <input data-testid="modal-create-delivery-order-items-status-packing-input"
                            type="checkbox"
                            checked={item?.status_packing ? true : false}
                            name={`items.${index}.status_packing`}
                            disabled={true}
                          />
                        </Table.Cell>
                      </Table.BodyRow>
                    ))}
                </Table.Body>
              </Table.Wrapper>
            </Table.Container>
            {payloads.status == 2 && (
              <>
                <div>Upload foto hasil packing di sini:</div>
                <input data-testid="modal-create-delivery-order-file-input"
                  type="file"
                  name="file"
                  onChange={(e) => fileChangeHandler(e)}
                />
                <p className="text-danger-600">
                  {errors.file && touched.file && errors.file}
                </p>
              </>
            )}
            {payloads.status == 3 && (
              <>
                <div className="bg-dark-100 p-4 rounded-md border border-dark-200">
                  <InputMolecule testId="modal-create-delivery-order-resi-number-input"
                    label="Nomor Resi Pengiriman"
                    type="text"
                    name="resi_number"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.resi_number}
                    errorMessage={
                      errors.resi_number &&
                      touched.resi_number &&
                      errors.resi_number
                    }
                    placeholder="contoh: 11LP1680679892004"
                  />
                </div>
                <p>
                  Tanggal Foto:{" "}
                  <span className="text-danger-600">
                    {new Date(
                      payloads && Number(payloads.image_date)
                    ).toLocaleString()}
                  </span>
                </p>
                <img
                  src={`${process.env.REACT_APP_API_BASE_URL}file/${payloads?.packing_picture}`}
                />
              </>
            )}

            {payloads.status == 4 && (
              <div>
                <img
                  src={`${process.env.REACT_APP_API_BASE_URL}file/${payloads?.packing_picture}`}
                />

                <div className="mt-2 flex items-center gap-2">
                  <p className="mb-0">No. Resi: {values.resi_number}</p>
                </div>
              </div>
            )}
            <div></div>
            <div
              className={`fixed bottom-2 left-0 sm:left-auto right-0 sm:right-4 sm:bottom-4 sm:w-auto z-40 flex justify-end border-t border-gray-100 bg-white sm:bg-transparent p-4 sm:p-0 dark:border-gray-700 dark:bg-gray-800 print:hidden sm:absolute sm:rounded-b-lg sm:border-0 ${payloads.status == 4 ? 'gap-2 grid grid-cols-2' : 'gap-2 flex'}`}
            >
              {payloads.status == 4 && (
                <button data-testid="modal-create-delivery-order-whatsapp-button"
                  type="button"
                  onClick={() => {
                    let phoneNumber = payloads?.MCustomer?.phone?.replace(/[^0-9]/g, '');
                    phoneNumber = phoneNumber?.replace(/^0/, '62') || '';

                    const message = encodeURIComponent(formatShippingNotification({
                      customerName: payloads?.MCustomer?.firstName,
                      courierName: getCourierName(payloads?.SalesOrder?.courier_package),
                      productName: payloads?.OrderItems?.map(item => 
                        `${item?.master_product?.name} (${item?.master_color?.name}, ${item?.master_size?.name}) (${item?.qty})`
                      ).filter(Boolean).join(', '),
                      trackingNumber: values?.resi_number,
                      trackingLink: getTrackingUrl(payloads?.SalesOrder?.courier_package, values?.resi_number)
                    }));
                    console.log(payloads?.OrderItems?.map(item => item?.master_product?.name).filter(Boolean).join(', '));
                    window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');
                  }}
                  className="inline-flex items-center justify-center rounded-md border border-green-700 bg-white px-3 py-1.5 text-sm font-medium text-green-700 hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-1 w-auto gap-2"
                >
                  <svg className="w-4 h-4 text-green-700" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.296-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.964-.941 1.162-.173.198-.349.223-.646.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.499.1-.202.049-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.077 4.492.709.306 1.263.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
                  </svg>
                  <span>Kirim WA</span>
                </button>
              )}
              <Button testId="modal-create-delivery-order-submit-button" type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Menyimpan...' : (payloads.status == 3
                  ? "Konfirmasi Pengiriman"
                  : payloads.status == 4
                    ? "Selesai"
                    : "Konfirmasi Packing")}
              </Button>
            </div>
          </form>
        )}
      </Formik>
    </ModalMolecule>
  );
}

export default CreateDeliveryPackingModal;
